import {
  ActionTestTarget,
  StepAction,
  TestTarget,
} from "../types/TestTarget";
import { get_test_target_parameter_counts } from "./actionConfig";

export type TargetCountStatus = {
  has_mismatch: boolean;
  actual_count: number;
  min_count: number;
  max_count: number;
};

const get_all_target_ids = (targets: TestTarget[]): Set<string> => {
  const ids = new Set<string>();
  const traverse = (target: TestTarget): void => {
    ids.add(target.id);
    if (target.children) {
      target.children.forEach(traverse);
    }
  };
  targets.forEach(traverse);
  return ids;
};

export const has_invalid_targets = (
  test_targets: ActionTestTarget[] | undefined,
  local_targets: TestTarget[]
): boolean => {
  if (!test_targets || test_targets.length === 0) {
    return false;
  }
  const all_target_ids = get_all_target_ids(local_targets);
  return test_targets.some(
    (target) =>
      !!target.test_target_id && !all_target_ids.has(target.test_target_id)
  );
};

export const get_target_count_status = (
  step_action: StepAction | undefined,
  test_targets: ActionTestTarget[] | undefined
): TargetCountStatus | null => {
  if (!step_action) {
    return null;
  }
  const { required_count, total_count } = get_test_target_parameter_counts(
    step_action.action
  );
  if (total_count === 0) {
    return null;
  }
  const actual_count = test_targets?.length ?? 0;
  const has_mismatch =
    actual_count < required_count || actual_count > total_count;
  return {
    has_mismatch,
    actual_count,
    min_count: required_count,
    max_count: total_count,
  };
};

export const get_target_warning_messages = (
  step_action: StepAction | undefined,
  test_targets: ActionTestTarget[] | undefined,
  local_targets: TestTarget[]
): string[] => {
  const warnings: string[] = [];
  const count_status = get_target_count_status(step_action, test_targets);
  if (count_status && count_status.has_mismatch) {
    const expected_count =
      count_status.min_count === count_status.max_count
        ? `${count_status.max_count}`
        : `${count_status.min_count}〜${count_status.max_count}`;
    const action_name = step_action?.action ?? "";
    warnings.push(
      `ターゲットが${count_status.actual_count}個設定されています。${action_name}に設定可能なターゲット数は${expected_count}個です。`
    );
  }
  if (has_invalid_targets(test_targets, local_targets)) {
    warnings.push("存在しないターゲットが設定されています");
  }
  return warnings;
};
